import { Tabs, Tab, Box, Badge } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import BugReportIcon from '@mui/icons-material/BugReport';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

export type ContentTab = 'homes' | 'chats' | 'bugs' | 'referrals';

interface ContentTabsProps {
  activeTab: ContentTab;
  onTabChange: (tab: ContentTab) => void;
  counts?: {
    flaggedHomes?: number;
    flaggedChats?: number;
    newBugs?: number;
    pendingReferrals?: number;
  };
}

/**
 * ContentTabs - Tab navigation for content moderation sections
 *
 * Features:
 * - 4 tabs: Homes, Chats, Bug Reports, Referrals
 * - Badge indicators for flagged/pending items
 * - Icon-based navigation
 */
export default function ContentTabs({
  activeTab,
  onTabChange,
  counts,
}: ContentTabsProps) {
  const tabs: Array<{
    value: ContentTab;
    label: string;
    icon: React.ReactElement;
    badgeCount?: number;
  }> = [
    {
      value: 'homes',
      label: 'Homes',
      icon: <HomeIcon />,
      badgeCount: counts?.flaggedHomes,
    },
    {
      value: 'chats',
      label: 'Chat Messages',
      icon: <ChatIcon />,
      badgeCount: counts?.flaggedChats,
    },
    {
      value: 'bugs',
      label: 'Bug Reports',
      icon: <BugReportIcon />,
      badgeCount: counts?.newBugs,
    },
    {
      value: 'referrals',
      label: 'Referrals',
      icon: <GroupAddIcon />,
      badgeCount: counts?.pendingReferrals,
    },
  ];

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => onTabChange(newValue as ContentTab)}
        variant="fullWidth"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              tab.badgeCount !== undefined && tab.badgeCount > 0 ? (
                <Badge badgeContent={tab.badgeCount} color="error">
                  {tab.label}
                </Badge>
              ) : (
                tab.label
              )
            }
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>
    </Box>
  );
}
